import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getEstabelecimentoAtualId } from '../lib/estabelecimento';

/**
 * Widget Cloudflare Turnstile (anti-robô invisível — item 1.2).
 * Modo "interaction-only": não aparece nada para o usuário a menos que o
 * desafio exija interação. Emite o token via onToken; o token é validado
 * server-side pelo Supabase Auth (Captcha protection habilitado no painel).
 */
const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) || '0x4AAAAAADjoL8Ry-FhS6TY3';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
    __turnstileLoading?: Promise<void>;
  }
}

function carregarScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (window.__turnstileLoading) return window.__turnstileLoading;
  window.__turnstileLoading = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Falha ao carregar Turnstile'));
    document.head.appendChild(s);
  });
  return window.__turnstileLoading;
}

export function Turnstile({ onToken }: { onToken: (token: string | null) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    let cancelado = false;

    async function lerVisivel(): Promise<boolean> {
      try {
        const { data } = await supabase
          .from('estabelecimentos')
          .select('configuracoes')
          .eq('id', getEstabelecimentoAtualId())
          .maybeSingle();
        return (data?.configuracoes as { turnstile_visivel?: boolean } | undefined)?.turnstile_visivel === true;
      } catch {
        return false;
      }
    }

    Promise.all([lerVisivel(), carregarScript()])
      .then(([visivel]) => {
        if (cancelado || !ref.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: SITE_KEY,
          appearance: visivel ? 'always' : 'interaction-only',
          'refresh-expired': 'auto', // pede um novo desafio sozinho quando o token expira
          callback: (token: string) => onToken(token),
          // Token expirou: zera e força um novo desafio para gerar outro token.
          'expired-callback': () => {
            onToken(null);
            if (widgetId.current && window.turnstile) {
              try { window.turnstile.reset(widgetId.current); } catch { /* noop */ }
            }
          },
          'error-callback': () => onToken(null),
        });
      })
      .catch(() => onToken(null));

    return () => {
      cancelado = true;
      if (widgetId.current && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={ref} className="flex justify-center" />;
}
